"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react"
import {
  en,
  de,
  landingEn,
  landingDe,
  guidesEn,
  guidesDe,
  resolve,
  interpolate,
} from "@bitebalance/i18n"
import type { Messages, Vars } from "@bitebalance/i18n"

export type Locale = "en" | "de"

// T10 landing-page copy lives in its own landing.en.ts/landing.de.ts
// pair (see those files) and is merged in here rather than into
// locales/en.ts or types.ts, so this dictionary index is the only
// shared file this merge touches. ADR-026's public guides (guides.en.ts/
// guides.de.ts) follow the identical pattern.
const dictionaries: Record<Locale, Messages> = {
  en: { ...en, ...landingEn, ...guidesEn },
  de: { ...de, ...landingDe, ...guidesDe },
}

const STORAGE_KEY = "bb_locale"

function detectDefaultLocale(): Locale {
  if (typeof window === "undefined") return "en"
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === "en" || stored === "de") return stored
  } catch {
    // localStorage unavailable (e.g. private mode) — fall through to detection.
  }
  const nav = window.navigator?.language?.toLowerCase() ?? "en"
  return nav.startsWith("de") ? "de" : "en"
}

/** Resolves a dot path like "auth.errors.generic" against a Messages tree. */

export interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  /** Translates a dot-path key (e.g. "nav.today"), with optional {{var}} interpolation. */
  t: (key: string, vars?: Vars) => string
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

/** `initialLocale` pins the starting locale where there is no browser to detect one (build-time prerendering). */
export function I18nProvider({ children, initialLocale }: PropsWithChildren<{ initialLocale?: Locale }>) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectDefaultLocale())

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = (next: Locale) => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Locale still applies for this session even if it can't persist.
    }
  }

  const t = useMemo(() => {
    const dict = dictionaries[locale]
    return (key: string, vars?: Vars) =>
      interpolate(resolve(dict as unknown as Record<string, unknown>, key), vars)
  }, [locale])

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error("useI18n must be used within an I18nProvider")
  }
  return ctx
}
